"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { fadeUpVariant, staggerContainer, staggerItem } from 'lib/utils';
import { Leaf, Flame, ShieldCheck, PackageCheck, Star, HeartHandshake } from 'lucide-react';
import Reveal from 'components/ui/Reveal';

export default function WhyStory() {
  const sections = [
    {
      title: "Seed Selection: Only the Best Makes the Cut",
      content: "Every Singaraa batch begins not in the roaster, but in the field. We hand-select only Jumbo-grade kernels - large, dense, evenly formed nuts with none of the bitterness or shriveling that comes from lesser grading. Every batch is inspected for size, color, and integrity before it ever touches the sand. This isn't a cost-saving compromise; it's a refusal to compromise at all.",
      icon: <Leaf className="w-8 h-8 text-singaraa-caramel" />
    },
    {
      title: "Artisanal Roasting: Precision Over Shortcuts",
      content: "Roasting Khari Sing correctly is a craft, not a formula. Our roasters manage moisture extraction with exacting care, allowing each kernel to lose exactly the right amount of internal moisture so the crunch stays clean rather than dry or chewy. Sodium is infused in careful balance - enough to awaken the peanut's natural sweetness, never so much that it masks it. The result is a nut with a deep, nutty, almost toasted-caramel undertone and a snap you can hear before you taste it.",
      icon: <Flame className="w-8 h-8 text-singaraa-caramel" />
    },
    {
      title: "100% Clean Label, Zero Compromise",
      content: "Singaraa contains no palm oil, no artificial flavorings, and no preservatives. It is non-GMO from source to seal, and naturally rich in plant-based protein - a snack that indulges without apology. What you taste is what grew in the ground: nothing added to disguise it, nothing needed to extend it.",
      icon: <ShieldCheck className="w-8 h-8 text-singaraa-caramel" />
    },
    {
      title: "The Gold Standard: Precision Vacuum Packaging",
      content: "Freshness is fragile. The moment a roasted peanut meets open air, oxidation begins - slowly stripping away the crunch, the aroma, the very character that made it special in the first place. This is why Singaraa is sealed exclusively in airtight, heavy-duty vacuum pouches, locking in roaster-fresh aroma and flavor at the peak of perfection. Whether your order lands in Mumbai, London, or New York, the pouch you open delivers the same crisp integrity as the day it left our roasting house - because freshness shouldn't have a shelf life determined by distance.",
      icon: <PackageCheck className="w-8 h-8 text-singaraa-caramel" />
    }
  ];

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#Fdfbf7] relative overflow-hidden">
      <div className="absolute inset-0 grain-overlay opacity-[0.15]" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-singaraa-caramel/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-singaraa-gold/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 relative z-10">
        
        {/* Intro Section */}
        <Reveal>
          <div className="max-w-4xl mx-auto text-center mb-12 sm:mb-16 md:mb-20">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-singaraa-dark font-bold mb-4 sm:mb-6">
              The Craft of Authentic Khari Sing
            </h2>
            <div className="w-16 sm:w-20 h-1 bg-singaraa-caramel mx-auto mb-6 sm:mb-8 lg:mb-10 rounded-full" />
            <div className="space-y-4 sm:space-y-6 text-singaraa-dark/80 font-body text-sm sm:text-base lg:text-lg leading-relaxed text-justify sm:text-center">
              <p>
                Long before "clean-label" became a promise brands had to earn, the farmers and roasters of Gujarat's Bharuch and Saurashtra belt were already living it. In the fertile, mineral-rich soil of this region, groundnuts grow naturally sweet and full-bodied - the product of generations of careful cultivation, not chemical shortcuts. 
              </p>
              <p>
                Once harvested, the kernels are treated in a traditional mineral brine and slow dry-roasted over clean sand or in age-old kilns - a method passed down through families, refined by hand, and never rushed. There is no oil in this process. Only heat, salt, time, and skill, coaxing out that iconic golden crunch that has defined <span className="italic font-semibold">Khari Sing</span> - India's original roasted peanut - for centuries.
              </p>
              <p className="font-medium text-singaraa-brown">
                To an international palate, this is not "just a peanut snack." It is a regional delicacy with the same pedigree and precision as a fine-roasted coffee bean or a cured Spanish almond - and Singaraa exists to bring that heritage, untouched and unhurried, to tables far beyond Gujarat.
              </p>
            </div>
          </div>
        </Reveal>

        {/* 4-Grid Features */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 lg:gap-10 mb-12 sm:mb-16 md:mb-20"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {sections.map((section, idx) => (
            <motion.div 
              key={idx} 
              variants={staggerItem}
              className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl border border-singaraa-caramel/15 shadow-sm hover:shadow-xl transition-all duration-300 group"
            >
              <div className="w-12 sm:w-16 h-12 sm:h-16 bg-singaraa-ivory rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300 border border-singaraa-caramel/10">
                {React.cloneElement(section.icon, { className: 'w-6 h-6 sm:w-8 sm:h-8 text-singaraa-caramel' })}
              </div>
              <h3 className="font-heading text-lg sm:text-xl lg:text-2xl text-singaraa-dark font-bold mb-3 sm:mb-4">
                {section.title}
              </h3>
              <p className="font-body text-singaraa-dark/70 text-sm sm:text-base leading-relaxed">
                {section.content}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Sensory Experience & Promise */}
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8 lg:gap-12">
          <Reveal delay={0.1} className="h-full">
            <div className="bg-singaraa-dark p-6 sm:p-8 lg:p-12 rounded-2xl sm:rounded-3xl h-full flex flex-col justify-center relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-singaraa-caramel/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <Star className="w-5 h-5 sm:w-6 sm:h-6 text-singaraa-caramel fill-singaraa-caramel" />
                  <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl text-singaraa-cream font-bold">The Sensory Experience</h3>
                </div>
                <p className="font-body text-singaraa-cream/80 text-sm sm:text-base lg:text-lg leading-relaxed">
                  Open a pack of Singaraa and the first thing you notice is the sound - a distinct, clean crack, not a soft give. Then the aroma: warm, toasted, faintly sweet. On the palate, a subtle salinity rises first, not to overpower but to frame the peanut's natural sweetness beneath it. The finish is clean, dry, and satisfying - no oily aftertaste, no lingering heaviness. It's a sensory signature that can't be replicated by mass-market roasting, only by patience and tradition.
                </p>
              </div>
            </div>
          </Reveal>
          
          <Reveal delay={0.3} className="h-full">
            <div className="bg-gradient-to-br from-singaraa-caramel to-singaraa-gold p-6 sm:p-8 lg:p-12 rounded-2xl sm:rounded-3xl h-full flex flex-col justify-center relative overflow-hidden group shadow-xl">
              <div className="absolute inset-0 pattern-dots opacity-20" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <HeartHandshake className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl text-white font-bold">Our Promise</h3>
                </div>
                <p className="font-body text-white/95 text-sm sm:text-base lg:text-lg leading-relaxed font-medium">
                  Singaraa is not manufactured. It is crafted. Every pouch carries the heritage of Gujarat's soil, the discipline of artisanal roasting, and an unwavering standard of freshness - sealed at the peak of perfection and delivered, without compromise, to wherever the world calls home.
                </p>
              </div>
            </div>
          </Reveal>
        </div>

      </div>
    </section>
  );
}
